import { withSessionId } from "../api/client";
import type { Message, Snapshot } from "../types";

export const REACTIONS = [
  { emoji: "👍", label: "Thumbs up" },
  { emoji: "❤️", label: "Heart" },
  { emoji: "😊", label: "Smile" },
  { emoji: "😂", label: "Laugh" },
  { emoji: "😮", label: "Surprised" },
  { emoji: "🎉", label: "Celebrate" },
] as const;

export function messageStatus(message: Message, snapshot?: Snapshot) {
  const peers =
    snapshot?.participants.filter(
      (peer) => peer.memberId !== message.memberId,
    ) ?? [];
  const assistant =
    snapshot?.kind === "P"
      ? peers.find((peer) => peer.role === "ASSISTANT")
      : undefined;
  if (snapshot && assistant) {
    // Work state belongs to the newest notice, not every earlier outgoing bubble.
    if (message.noticeSeq === snapshot.lastNoticeSeq) {
      if (snapshot.workStatus === "QUEUED") return "Queued";
      if (snapshot.workStatus === "WORKING") return "Working";
    }
    if (
      BigInt(snapshot.peerLastRead) >= BigInt(message.noticeSeq) ||
      BigInt(snapshot.peerLastGot) >= BigInt(message.eventNo)
    )
      return `Received by ${assistant.name}`;
    return "Saved to server";
  }
  if (snapshot?.kind === "G") {
    const humans = peers.filter((peer) => peer.role !== "ASSISTANT");
    if (
      humans.some((peer) => BigInt(peer.lastRead) >= BigInt(message.noticeSeq))
    )
      return "Read";
    if (humans.some((peer) => BigInt(peer.lastGot) >= BigInt(message.eventNo)))
      return "Delivered";
    return "Saved to server";
  }
  if (snapshot && BigInt(snapshot.peerLastRead) >= BigInt(message.noticeSeq))
    return "Read";
  if (snapshot && BigInt(snapshot.peerLastGot) >= BigInt(message.eventNo))
    return "Delivered";
  return "Saved to server";
}

export function messageTimestamp(
  message: Pick<Message, "datetime" | "utcOffset">,
) {
  const value = message.datetime;
  // Legacy datetimes carry a separate offset. Never reinterpret them in the
  // browser's zone before the offset has been applied.
  const suffix = /(?:Z|[+-]\d\d:\d\d)$/.test(value)
    ? ""
    : `${message.utcOffset < 0 ? "-" : "+"}${String(Math.floor(Math.abs(message.utcOffset) / 60)).padStart(2, "0")}:${String(Math.abs(message.utcOffset) % 60).padStart(2, "0")}`;
  const date = new Date(value.replace(" ", "T") + suffix);
  return date;
}

export function messageDate(message: Message) {
  const date = messageTimestamp(message);
  return Number.isNaN(date.getTime())
    ? message.datetime
    : date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

/**
 * Media must remain behind the same-origin membership check. The element loads it itself, so it carries the
 * stored session id as `?sessionid=` like every chat request (Safari can drop the cookie).
 */
export function privateMediaUrl(url: string) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin &&
      parsed.pathname.startsWith("/api/secretmissions/webchat/chats/")
      ? withSessionId(parsed.href)
      : undefined;
  } catch {
    return undefined;
  }
}

export function transcriptText(messages: Message[], memberId: string) {
  return messages
    .map(
      (message) =>
        `${messageDate(message)} · ${message.memberId === memberId ? "You" : message.memberName}${message.edited ? " (edited)" : ""}\n${message.deleted ? "Message removed" : [message.replyQuote ? `> ${message.replyQuote}` : "", message.text, ...message.attachments.map((attachment) => `[${attachment.type.toLowerCase()}: ${attachment.name}]`)].filter(Boolean).join("\n")}`,
    )
    .join("\n\n");
}

export function downloadTranscript(text: string) {
  const url = URL.createObjectURL(
    new Blob([text], { type: "text/plain;charset=utf-8" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `handler-conversation-${new Date().toISOString().slice(0, 10)}.txt`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Group receipt cursors follow the same notice/event distinctions as private chats. */
export function messageReaders(message: Message, snapshot?: Snapshot) {
  return (snapshot?.participants ?? []).filter(
    (participant) =>
      participant.memberId !== message.memberId &&
      participant.role !== "ASSISTANT" &&
      BigInt(participant.lastRead) >= BigInt(message.noticeSeq),
  );
}

export function messageDay(message: Message) {
  const date = messageTimestamp(message);
  return Number.isNaN(date.getTime()) ? "" : date.toDateString();
}

export function dayLabel(message: Message, now = new Date()) {
  const date = messageTimestamp(message);
  if (Number.isNaN(date.getTime())) return "";
  if (date.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function groupsWithPrevious(message: Message, previous?: Message) {
  if (
    !previous ||
    previous.memberId !== message.memberId ||
    previous.deleted ||
    message.deleted ||
    messageDay(message) !== messageDay(previous)
  )
    return false;
  const elapsed =
    messageTimestamp(message).getTime() - messageTimestamp(previous).getTime();
  return elapsed >= 0 && elapsed < 5 * 60 * 1000;
}

export function listTime(
  message: Pick<Message, "datetime" | "utcOffset">,
  now = new Date(),
) {
  const date = messageTimestamp(message);
  if (Number.isNaN(date.getTime())) return "";
  return date.toDateString() === now.toDateString()
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
}
